<template>
  <div class="flex flex-col h-full min-h-0">
    <ChatUserSearchBar
      placeholder="Find someone to message..."
      @select="onStartDM"
    />

    <!-- New group button — opens the multi-select dialog -->
    <div class="px-3 py-2 border-b border-white/5">
      <button
        type="button"
        class="w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-xs font-semibold text-text-muted hover:text-white transition-colors"
        @click="groupDialogOpen = true"
      >
        <el-icon size="12"><Plus /></el-icon>
        {{ $t('chat.groups.new_group_button') }}
      </button>
    </div>

    <div class="flex-1 overflow-y-auto scrollbar-thin scrollbar-track-transparent">
      <!-- Label rooms — one chat per label, membership derived from release
           credits + label role. Only rendered when the user belongs to at
           least one label room. -->
      <template v-if="labels.length > 0">
        <div class="px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-text-muted">
          Label rooms
        </div>
        <button
          v-for="l in labels"
          :key="l.labelId"
          type="button"
          class="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/[0.03] transition-colors text-left disabled:opacity-60 disabled:cursor-not-allowed"
          :class="{ 'bg-white/[0.04]': l.threadId && activeThreadId === l.threadId }"
          :disabled="entering === l.labelId"
          @click="onEnterLabel(l.labelId)"
        >
          <div class="w-9 h-9 rounded-lg bg-gradient-to-br from-primary/15 to-purple-500/10 border border-white/10 flex items-center justify-center text-xs font-bold text-primary shrink-0 overflow-hidden">
            <img v-if="l.logo" :src="l.logo" :alt="l.name" class="w-full h-full object-cover" />
            <span v-else>{{ (l.name || '?').slice(0, 1).toUpperCase() }}</span>
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center justify-between gap-2">
              <span class="text-sm font-bold text-white truncate">{{ l.name }}</span>
              <span
                v-if="l.unread > 0"
                class="text-[10px] font-bold bg-primary text-black rounded-full px-1.5 py-0.5 leading-none shrink-0"
              >{{ l.unread > 99 ? '99+' : l.unread }}</span>
            </div>
            <p class="text-[11px] text-text-muted truncate">
              {{ l.lastMessagePreview || 'Tap to open' }}
            </p>
          </div>
        </button>
        <div class="h-px bg-white/5 mx-3 my-2"></div>
      </template>

      <div v-if="threadsLoading && threads.length === 0" class="flex justify-center py-8">
        <div class="w-6 h-6 border-2 border-white/10 border-t-primary rounded-full animate-spin"></div>
      </div>

      <div v-else-if="threads.length === 0 && labels.length === 0" class="text-center py-12 px-4">
        <p class="text-sm text-text-muted">No conversations yet</p>
        <p class="text-[11px] text-text-dim mt-1">Search above to find someone and start a DM.</p>
      </div>

      <ChatThreadRow
        v-for="t in threads"
        :key="t.id"
        :thread="t"
        :active="activeThreadId === t.id"
        @click="$emit('open-thread', t.id)"
      />
    </div>

    <ChatGroupCreateDialog v-model="groupDialogOpen" />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { Plus } from '@element-plus/icons-vue'
import { useChatThreads } from '~/composables/chat/useChatThreads'
import { useChatLabels } from '~/composables/chat/useChatLabels'

const emit = defineEmits(['open-thread'])

const { threads, threadsLoading, threadsLoaded, activeThreadId, loadThreads, startDM } = useChatThreads()
const { labels, loaded: labelsLoaded, entering, loadLabels, enterLabel, markLabelRead } = useChatLabels()
const groupDialogOpen = ref(false)

onMounted(() => {
  if (!threadsLoaded.value) loadThreads()
  if (!labelsLoaded.value) loadLabels()
})

async function onStartDM(user) {
  const threadId = await startDM(user.id)
  if (threadId) emit('open-thread', threadId)
}

async function onEnterLabel(labelId) {
  const threadId = await enterLabel(labelId)
  if (threadId) {
    markLabelRead(labelId)
    emit('open-thread', threadId)
  }
}
</script>
